import React, { useMemo } from "react"

import { ProductConfiguratorStepType } from "shared/types/ProductConfiguration"
import { PropertyList } from "shared/ui/property-list/PropertyList"

import { ProductExternalDimensioning } from "entities/products/ui/product-external-dimensioning/ProductExternalDimensioning"

import { useProductConfiguratorContext } from "../../../model/ProductConfiguratorContext"
import { useProductConfiguratorProductInfoContext } from "../../../model/ProductConfiguratorProductInfoContext"
import { ProductConfiguratorSummaryCardAddons } from "../../summary-card/ProductConfiguratorSummaryCardAddons"

const SUMMARY_STEP_TYPES: ProductConfiguratorStepType[] = [
  ProductConfiguratorStepType.DIMENSIONING,
  ProductConfiguratorStepType.CONFIGURATION,
  ProductConfiguratorStepType.ADDONS
]

export interface ProductConfiguratorStepSummary {
  step: ProductConfiguratorStepType
  title: string
  subtitle: string
  content: React.ReactNode
  onEdit: () => void
}

export function useProductConfiguratorStepSummaries(): ProductConfiguratorStepSummary[] {
  const { steps, activateStep } = useProductConfiguratorContext()
  const { productInformation, isLoadingProductInformation } =
    useProductConfiguratorProductInfoContext()

  const isLoading = Boolean(isLoadingProductInformation && !productInformation)

  return useMemo<ProductConfiguratorStepSummary[]>(() => {
    const resolveContent = (
      stepType: ProductConfiguratorStepType
    ): React.ReactNode => {
      switch (stepType) {
        case ProductConfiguratorStepType.DIMENSIONING:
          return productInformation?.dimensioning ? (
            <ProductExternalDimensioning
              productInformation={productInformation}
              isLoading={isLoading}
            />
          ) : null
        case ProductConfiguratorStepType.CONFIGURATION:
          return productInformation?.attributes?.length ? (
            <PropertyList
              list={productInformation.attributes}
              isLoading={isLoading}
            />
          ) : null
        case ProductConfiguratorStepType.ADDONS:
          return (
            <ProductConfiguratorSummaryCardAddons
              addons={productInformation?.addons}
              isLoading={isLoading}
            />
          )
        default:
          return null
      }
    }

    return steps
      .filter(step => SUMMARY_STEP_TYPES.includes(step.value) && !step.status)
      .map(step => ({
        step: step.value,
        title: step.label,
        subtitle: step.description,
        content: resolveContent(step.value),
        onEdit: () => activateStep(step.value)
      }))
      .filter(summary => summary.content !== null)
  }, [steps, productInformation, isLoading, activateStep])
}
